import { useMutation, useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { api } from '@/api/client';
import { AppIcon } from '@/components/AppIcon';
import {
  ActionBar,
  DataGrid,
  DetailPane,
  SplitView,
  StatusBar,
  readStored,
  writeStored,
  type DesktopAction,
  type DetailTab,
  type GridColumn,
} from '@/components/desktop';
import { HistoryPanel } from '@/components/documents';
import { DraftNotice } from '@/components/DraftNotice';
import { ActionDialog, type ActionField } from '@/components/hr/ActionDialog';
import { useDraft } from '@/stores/drafts';
import { StatePill } from '@/components/hr/StatePill';
import { stateLabel } from '@/components/hr/states';
import { printTable } from '@/components/hrPrint';
import {
  ErrorNote,
  Field,
  FormActions,
  FormGroup,
  FormPanel,
  OkNote,
  Pager,
  Select,
  TextArea,
  errorMessage,
  shortDate,
  todayIso,
} from '@/components/ui';
import { employeeLabel, hrGet, useEmployeeOptions, useHrInvalidation, useHrLookups } from '@/hooks/hr';
import { useAuth } from '@/stores/auth';
import { farmScopeQuery, useFarmScope } from '@/stores/farmScope';
import { useScreenIntent } from '@/stores/shellNav';
import { daysText, yearOptions } from './hrScreenHelpers';
import { LeaveBalances, type BalanceItem } from './LeaveBalances';
import './leave.css';

const PAGE_SIZE = 100;
const VIEW_KEY = 'poultry.hr.leave.view';

type LeaveState = 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';

export interface LeaveRow {
  id: string;
  documentNo: string;
  employeeId: string;
  employeeCode: string;
  employeeName: string;
  farmCode: string | null;
  leaveTypeId: string;
  leaveType: string;
  isPaid: boolean;
  fromDate: string;
  toDate: string;
  days: string;
  reason: string | null;
  status: string;
  approval: string;
  state: LeaveState;
  decisionNote: string | null;
  decidedAt: string | null;
  raisedByYou: boolean;
  version: number;
  createdAt: string;
}

const STATES: Array<{ value: LeaveState; label: string }> = [
  { value: 'PENDING', label: 'Waiting for a decision' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

type View = 'records' | 'balances';

type Dialog = { kind: 'approve' | 'reject' | 'cancel'; row: LeaveRow } | null;

function range(r: { fromDate: string; toDate: string }): string {
  return r.fromDate === r.toDate ? shortDate(r.fromDate) : `${shortDate(r.fromDate)} to ${shortDate(r.toDate)}`;
}

/**
 * Leave: leave recorded ahead of time (one person records it, someone else
 * approves it, and its days then go on the attendance register as L), and
 * how much of each kind of leave everyone has left this year.
 */
export function Leave() {
  const intent = useScreenIntent('hr-leave');
  const [view, setViewState] = useState<View>(() => (readStored<string>(VIEW_KEY, 'records') === 'balances' ? 'balances' : 'records'));
  const [personId, setPersonId] = useState<string | null>(intent?.employeeId ?? null);
  const people = useEmployeeOptions('CURRENT', personId !== null);
  const person = personId ? people.data?.items.find((e) => e.id === personId) : undefined;

  function setView(next: View) {
    setViewState(next);
    writeStored(VIEW_KEY, next);
  }

  const toggle = (
    <div className="viewtoggle" role="tablist" aria-label="View">
      {(['records', 'balances'] as const).map((v) => (
        <button
          key={v}
          type="button"
          role="tab"
          aria-selected={view === v}
          className={`viewtoggle__btn${view === v ? ' viewtoggle__btn--on' : ''}`}
          onClick={() => setView(v)}
        >
          {v === 'records' ? 'Leave records' : 'Balances'}
        </button>
      ))}
    </div>
  );

  const personChip = personId ? (
    <span className="leave__person">
      <span className="pill">Only {person ? `${person.code} — ${person.nameEn}` : 'one person'}</span>
      <button type="button" className="linkbutton" onClick={() => setPersonId(null)}>
        Show everyone
      </button>
    </span>
  ) : null;

  return view === 'records' ? (
    <LeaveRecords
      toggle={toggle}
      personChip={personChip}
      personId={personId}
      startWithForm={intent?.action === 'create'}
    />
  ) : (
    <LeaveBalances toggle={toggle} personChip={personChip} personId={personId} />
  );
}

function LeaveRecords({
  toggle,
  personChip,
  personId,
  startWithForm,
}: {
  toggle: ReactNode;
  personChip: ReactNode;
  personId: string | null;
  startWithForm: boolean;
}) {
  const { can } = useAuth();
  const invalidate = useHrInvalidation();
  const farmIds = useFarmScope((s) => s.selectedFarmIds);
  const thisYear = Number(todayIso().slice(0, 4));
  const [state, setState] = useState<string>('');
  const [year, setYear] = useState(String(thisYear));
  const [skip, setSkip] = useState(0);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [tab, setTab] = useState('details');
  const [form, setForm] = useState<{ mode: 'create' } | { mode: 'edit'; row: LeaveRow } | null>(() =>
    startWithForm ? { mode: 'create' } : null,
  );
  const [dialog, setDialog] = useState<Dialog>(null);
  const [note, setNote] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['hr', 'leave', 'list', farmIds, state, year, personId, skip],
    queryFn: () =>
      hrGet<{ total: number; items: LeaveRow[] }>('/api/v1/hr/leave', {
        ...farmScopeQuery(farmIds),
        state,
        year,
        employeeId: personId,
        skip,
        take: PAGE_SIZE,
      }),
  });
  const rows = list.data?.items ?? [];
  const selected = rows.find((r) => r.id === selectedKey) ?? null;

  const remove = useMutation({
    mutationFn: (row: LeaveRow) => api.delete(`/api/v1/hr/leave/${row.id}?version=${row.version}`),
    onSuccess: (_, row) => {
      invalidate();
      setSelectedKey(null);
      setProblem(null);
      setNote(`${row.documentNo} deleted.`);
    },
    onError: (e) => setProblem(errorMessage(e, 'Could not delete it')),
  });

  function done(message: string) {
    invalidate();
    setProblem(null);
    setNote(message);
  }

  function confirmDelete(row: LeaveRow) {
    if (window.confirm(`Delete ${row.documentNo}? It has not been decided yet, so nothing else changes.`)) remove.mutate(row);
  }

  function printSlip(r: LeaveRow) {
    printTable({
      title: `Leave ${r.documentNo} — ${r.employeeCode} ${r.employeeName}`,
      columns: [
        { key: 'label', header: '' },
        { key: 'value', header: '' },
      ],
      rows: [
        { label: 'Person', value: `${r.employeeCode} — ${r.employeeName}` },
        { label: 'Farm', value: r.farmCode ?? 'Head office' },
        { label: 'Leave', value: `${r.leaveType}${r.isPaid ? '' : ' (unpaid)'}` },
        { label: 'Dates', value: range(r) },
        { label: 'Days', value: daysText(r.days) },
        { label: 'Why', value: r.reason ?? '' },
        { label: 'State', value: stateLabel(r.state) },
        { label: 'Decision note', value: r.decisionNote ?? '' },
      ],
      signatures: ['Employee', 'Supervisor', 'Approved by'],
    });
  }

  function printList() {
    printTable({
      title: `Leave records ${year}${state ? ` — ${stateLabel(state)}` : ''}`,
      columns: [
        { key: 'documentNo', header: 'Number' },
        { key: 'person', header: 'Person' },
        { key: 'farm', header: 'Farm' },
        { key: 'leaveType', header: 'Leave' },
        { key: 'fromDate', header: 'From', kind: 'date' },
        { key: 'toDate', header: 'To', kind: 'date' },
        { key: 'days', header: 'Days', kind: 'number' },
        { key: 'state', header: 'State' },
        { key: 'reason', header: 'Why' },
      ],
      rows: rows.map((r) => ({
        documentNo: r.documentNo,
        person: `${r.employeeCode} — ${r.employeeName}`,
        farm: r.farmCode ?? 'Head office',
        leaveType: r.leaveType,
        fromDate: r.fromDate,
        toDate: r.toDate,
        days: r.days,
        state: stateLabel(r.state),
        reason: r.reason ?? '',
      })),
    });
  }

  function rowActions(row: LeaveRow): DesktopAction[] {
    const waiting = row.state === 'PENDING';
    return [
      {
        label: 'Edit',
        icon: 'edit',
        shortcut: 'F2',
        onSelect: () => setForm({ mode: 'edit', row }),
        allowed: can('hr.leave.update'),
        enabled: waiting,
        reason: 'Only leave still waiting for a decision can be changed',
      },
      {
        label: 'Approve',
        icon: 'approve',
        onSelect: () => setDialog({ kind: 'approve', row }),
        allowed: can('hr.leave.approve'),
        enabled: waiting && !row.raisedByYou,
        reason: row.raisedByYou ? 'Whoever recorded it cannot approve it' : 'Only leave waiting for a decision can be approved',
        separatorBefore: true,
      },
      {
        label: 'Reject',
        icon: 'reject',
        onSelect: () => setDialog({ kind: 'reject', row }),
        allowed: can('hr.leave.reject'),
        enabled: waiting && !row.raisedByYou,
        reason: row.raisedByYou ? 'Whoever recorded it cannot reject it' : 'Only leave waiting for a decision can be rejected',
      },
      {
        label: 'Cancel approved leave',
        icon: 'reverse',
        danger: true,
        onSelect: () => setDialog({ kind: 'cancel', row }),
        allowed: can('hr.leave.approve'),
        enabled: row.state === 'APPROVED',
        reason: 'Only approved leave can be cancelled',
      },
      {
        label: 'Delete',
        icon: 'remove',
        danger: true,
        shortcut: 'Del',
        onSelect: () => confirmDelete(row),
        allowed: can('hr.leave.delete'),
        enabled: waiting,
        reason: 'Only leave still waiting for a decision can be deleted',
      },
      {
        label: 'Print',
        icon: 'print',
        onSelect: () => printSlip(row),
        separatorBefore: true,
      },
    ];
  }

  const toolbar: DesktopAction[] = [
    {
      label: 'Record leave',
      icon: 'add',
      primary: true,
      shortcut: 'Ins',
      onSelect: () => setForm({ mode: 'create' }),
      allowed: can('hr.leave.create'),
    },
    ...(selected
      ? rowActions(selected)
      : [
          {
            label: 'Approve',
            icon: 'approve' as const,
            onSelect: () => {},
            allowed: can('hr.leave.approve'),
            enabled: false,
            reason: 'Choose one first',
            separatorBefore: true,
          },
        ]),
    {
      label: 'Print list',
      icon: 'print',
      onSelect: printList,
      enabled: rows.length > 0,
      reason: 'Nothing to print',
      separatorBefore: true,
    },
  ];

  const columns: Array<GridColumn<LeaveRow>> = [
    {
      key: 'doc',
      header: 'Number',
      width: 140,
      render: (r) => (
        <span className="withicon">
          <AppIcon kind="leave" size={14} />
          <span className="mono">{r.documentNo}</span>
        </span>
      ),
    },
    { key: 'person', header: 'Person', width: 210, render: (r) => `${r.employeeCode} — ${r.employeeName}` },
    { key: 'farm', header: 'Farm', width: 80, render: (r) => <span className="dim">{r.farmCode ?? 'Head office'}</span> },
    {
      key: 'leave',
      header: 'Leave',
      width: 150,
      render: (r) => (
        <>
          {r.leaveType}
          {!r.isPaid && <span className="dim"> (unpaid)</span>}
        </>
      ),
    },
    { key: 'from', header: 'From', width: 100, render: (r) => shortDate(r.fromDate) },
    { key: 'to', header: 'To', width: 100, render: (r) => shortDate(r.toDate) },
    { key: 'days', header: 'Days', align: 'end', width: 60, render: (r) => daysText(r.days) },
    { key: 'state', header: 'State', width: 180, render: (r) => <StatePill state={r.state} /> },
    { key: 'reason', header: 'Why', width: 220, render: (r) => <span className="dim">{r.reason ?? '—'}</span> },
  ];

  if (form) {
    return (
      <div className="workbench">
        <div className="workbench__main workbench__main--form">
          <LeaveForm
            key={form.mode === 'edit' ? form.row.id : 'create'}
            editing={form.mode === 'edit' ? form.row : undefined}
            presetEmployeeId={personId}
            onCancel={() => setForm(null)}
            onSaved={(message, id) => {
              setForm(null);
              if (id) setSelectedKey(id);
              done(message);
            }}
          />
        </div>
      </div>
    );
  }

  const tabs: DetailTab[] = selected
    ? [
        {
          id: 'details',
          label: 'Details',
          icon: 'details',
          content: (
            <div className="stack">
              <dl className="detaillist">
                <dt>Person</dt>
                <dd>
                  {selected.employeeCode} — {selected.employeeName}
                </dd>
                <dt>Farm</dt>
                <dd>{selected.farmCode ?? 'Head office'}</dd>
                <dt>Leave</dt>
                <dd>
                  {selected.leaveType} {selected.isPaid ? '' : '(unpaid)'}
                </dd>
                <dt>Dates</dt>
                <dd>{range(selected)}</dd>
                <dt>Days</dt>
                <dd>
                  {daysText(selected.days)}
                  {Number(selected.days) === 0.5 && ' (half day)'}
                </dd>
                <dt>State</dt>
                <dd>
                  <StatePill state={selected.state} />
                </dd>
                <dt>Why</dt>
                <dd>{selected.reason ?? '—'}</dd>
                {selected.decisionNote && (
                  <>
                    <dt>Decision note</dt>
                    <dd>{selected.decisionNote}</dd>
                  </>
                )}
                {selected.decidedAt && (
                  <>
                    <dt>Decided on</dt>
                    <dd>{shortDate(selected.decidedAt)}</dd>
                  </>
                )}
                <dt>Recorded on</dt>
                <dd>
                  {shortDate(selected.createdAt)}
                  {selected.raisedByYou && <span className="dim"> (by you)</span>}
                </dd>
                <dt>Balance</dt>
                <dd>
                  <BalanceLine
                    employeeId={selected.employeeId}
                    leaveTypeId={selected.leaveTypeId}
                    year={Number(selected.fromDate.slice(0, 4))}
                  />
                </dd>
              </dl>
            </div>
          ),
        },
        {
          id: 'history',
          label: 'History',
          icon: 'history',
          content: <HistoryPanel path={`/api/v1/hr/leave/${selected.id}/history`} />,
        },
      ]
    : [];

  const waiting = rows.filter((r) => r.state === 'PENDING').length;

  return (
    <div className="workbench">
      <SplitView
        id="hr-leave"
        direction="column"
        initial={420}
        firstLabel="leave list"
        first={
          <div className="workbench__main">
            <div className="filterbar leave__filters">
              <Select
                label="Show"
                value={state}
                onChange={(v) => {
                  setState(v);
                  setSkip(0);
                }}
                options={STATES}
                placeholder="All"
              />
              <Select
                label="Year"
                value={year}
                onChange={(v) => {
                  setYear(v);
                  setSkip(0);
                }}
                options={yearOptions(thisYear, 4, 1)}
                placeholder="Every year"
              />
              {personChip}
              <span className="leave__spacer" />
              {toggle}
            </div>
            <ActionBar actions={toolbar} />
            {note && <OkNote message={note} />}
            {problem && <ErrorNote message={problem} />}
            {list.error && <ErrorNote message={list.error.message} />}
            <DataGrid
              id="hr-leave"
              label="Leave records"
              columns={columns}
              rows={rows}
              rowKey={(r) => r.id}
              selectedKey={selectedKey}
              onSelect={(k) => {
                setSelectedKey(k);
                setNote(null);
              }}
              onOpen={(r) => {
                setSelectedKey(r.id);
                setTab('details');
              }}
              onEdit={(r) => {
                if (r.state === 'PENDING' && can('hr.leave.update')) setForm({ mode: 'edit', row: r });
              }}
              onDelete={(r) => {
                if (r.state === 'PENDING' && can('hr.leave.delete')) confirmDelete(r);
              }}
              actions={rowActions}
              loading={list.isPending}
              empty={state ? `No leave ${stateLabel(state).toLowerCase()} in ${year || 'any year'}.` : 'No leave recorded yet.'}
            />
            <Pager skip={skip} take={PAGE_SIZE} total={list.data?.total ?? 0} onChange={setSkip} />
          </div>
        }
        second={
          <DetailPane
            tabs={tabs}
            activeTab={tab}
            onTabChange={setTab}
            empty="Choose a leave record to see its details and history."
            title={
              selected && (
                <span className="withicon">
                  <AppIcon kind="leave" size={14} />
                  <span className="mono">{selected.documentNo}</span> {selected.employeeName}
                </span>
              )
            }
          />
        }
      />
      <StatusBar>
        <span>{list.data?.total ?? 0} shown</span>
        {waiting > 0 && <span>{waiting} waiting for a decision</span>}
        <span>{selected ? `Chosen: ${selected.documentNo}` : 'Nothing chosen'}</span>
      </StatusBar>

      {dialog && <LeaveDialog dialog={dialog} onClose={() => setDialog(null)} onDone={done} />}
    </div>
  );
}

function LeaveDialog({
  dialog,
  onClose,
  onDone,
}: {
  dialog: NonNullable<Dialog>;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const { row } = dialog;
  const base = `/api/v1/hr/leave/${row.id}`;
  const what = `${row.employeeName} — ${row.leaveType}, ${range(row)} (${daysText(row.days)} ${Number(row.days) === 1 ? 'day' : 'days'})`;
  const configs: Record<
    NonNullable<Dialog>['kind'],
    {
      title: string;
      icon: 'approve' | 'reject' | 'reverse';
      intro: string;
      fields: ActionField[];
      submit: string;
      danger?: boolean;
      run: (v: Record<string, string>) => Promise<unknown>;
      message: string;
    }
  > = {
    approve: {
      title: `Approve ${row.documentNo}`,
      icon: 'approve',
      intro: `${what}. Once approved, these days go on the attendance register as leave.`,
      fields: [{ key: 'comment', label: 'Comment', type: 'textarea' }],
      submit: 'Approve',
      run: (v) => api.post(`${base}/approve`, { comment: v.comment?.trim() || undefined }),
      message: `${row.documentNo} approved. Its days are now on the register as leave.`,
    },
    reject: {
      title: `Reject ${row.documentNo}`,
      icon: 'reject',
      intro: what,
      fields: [{ key: 'comment', label: 'Why', type: 'textarea', required: true }],
      submit: 'Reject',
      danger: true,
      run: (v) => api.post(`${base}/reject`, { comment: v.comment?.trim() }),
      message: `${row.documentNo} rejected.`,
    },
    cancel: {
      title: `Cancel ${row.documentNo}`,
      icon: 'reverse',
      intro: `${what}. Its days come off the attendance register and go back to the balance.`,
      fields: [{ key: 'reason', label: 'Why', type: 'textarea', required: true }],
      submit: 'Cancel the leave',
      danger: true,
      run: (v) => api.post(`${base}/cancel`, { reason: v.reason?.trim() }),
      message: `${row.documentNo} cancelled. Its days are off the register.`,
    },
  };
  const c = configs[dialog.kind];
  return (
    <ActionDialog
      title={c.title}
      icon={c.icon}
      intro={c.intro}
      fields={c.fields}
      submitLabel={c.submit}
      danger={c.danger ?? false}
      onClose={onClose}
      onSubmit={async (v) => {
        await c.run(v);
        onDone(c.message);
      }}
    />
  );
}

/** How much of one kind of leave someone has left in a year. */
function usePersonBalances(employeeId: string, year: number) {
  return useQuery({
    queryKey: ['hr', 'leave', 'balances', 'person', employeeId, year],
    queryFn: () => hrGet<{ year: number; items: BalanceItem[] }>('/api/v1/hr/leave/balances', { employeeId, year }),
    enabled: employeeId !== '' && year >= 2000,
  });
}

function BalanceLine({ employeeId, leaveTypeId, year }: { employeeId: string; leaveTypeId: string; year: number }) {
  const q = usePersonBalances(employeeId, year);
  if (q.isPending) return <span className="muted">Loading…</span>;
  if (q.isError) return <span className="muted">Not available</span>;
  const b = q.data.items[0]?.balances.find((x) => x.leaveTypeId === leaveTypeId);
  if (!b) return <span className="muted">No balance for this leave</span>;
  if (!b.isPaid) return <span>Unpaid leave — no balance is used.</span>;
  return (
    <span>
      <strong>{daysText(b.remaining)}</strong> left in {year} ({daysText(b.entitled)} entitled + {daysText(b.carriedForward)} brought forward
      {Number(b.adjusted) !== 0 && ` ${Number(b.adjusted) > 0 ? '+' : '−'} ${daysText(Math.abs(Number(b.adjusted)))} adjusted`} − {daysText(b.taken)} taken)
      {Number(b.pending) > 0 && `, ${daysText(b.pending)} waiting for a decision`}
    </span>
  );
}

function calendarDays(from: string, to: string): number {
  if (!from || !to || to < from) return 0;
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000) + 1;
}

function LeaveForm({
  editing,
  presetEmployeeId,
  onCancel,
  onSaved,
}: {
  editing: LeaveRow | undefined;
  presetEmployeeId: string | null;
  onCancel: () => void;
  onSaved: (message: string, id?: string) => void;
}) {
  const people = useEmployeeOptions('CURRENT', !editing);
  const lookups = useHrLookups();
  const today = todayIso();
  const [v, setV] = useState(() => ({
    employeeId: editing?.employeeId ?? presetEmployeeId ?? '',
    leaveTypeId: editing?.leaveTypeId ?? '',
    fromDate: editing?.fromDate ?? today,
    toDate: editing?.toDate ?? today,
    halfDay: editing ? Number(editing.days) === 0.5 : false,
    reason: editing?.reason ?? '',
  }));
  const [problem, setProblem] = useState<string | null>(null);

  // Brought back after a reload, keyed so editing one record never offers
  // another one's typing.
  const [blankValues] = useState(() => v);
  const draft = useDraft(editing ? `leave.edit.${editing.id}` : 'leave.new', v, (saved) => setV(saved));

  function startAgain() {
    setV(blankValues);
    setProblem(null);
  }
  const set = (key: 'employeeId' | 'leaveTypeId' | 'fromDate' | 'toDate' | 'reason') => (value: string) =>
    setV((s) => {
      const next = { ...s, [key]: value };
      if (next.fromDate !== next.toDate) next.halfDay = false;
      return next;
    });

  const year = Number(v.fromDate.slice(0, 4)) || Number(today.slice(0, 4));
  const balances = usePersonBalances(v.employeeId, year);
  const personBalances = balances.data?.items[0]?.balances;
  const person = people.data?.items.find((e) => e.id === v.employeeId);
  // The balances list only the kinds of leave that apply to this person; use
  // them once known, otherwise every kind (daily-wage staff get fewer).
  const allTypes = lookups.data?.leaveTypes ?? [];
  const typeOptions = personBalances
    ? personBalances.map((b) => ({ value: b.leaveTypeId, label: `${b.nameEn}${b.isPaid ? '' : ' (unpaid)'}` }))
    : allTypes
        .filter((t) => person?.workerType !== 'DAILY' || t.forDailyWorkers)
        .map((t) => ({ value: t.id, label: `${t.nameEn}${t.isPaid ? '' : ' (unpaid)'}` }));
  const balance = personBalances?.find((b) => b.leaveTypeId === v.leaveTypeId);
  const span = v.halfDay ? 0.5 : calendarDays(v.fromDate, v.toDate);
  const available = balance ? Number(balance.remaining) - Number(balance.pending) + (editing && editing.leaveTypeId === balance.leaveTypeId ? Number(editing.days) : 0) : null;
  const short = balance?.isPaid && available !== null && span > available;

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        leaveTypeId: v.leaveTypeId,
        fromDate: v.fromDate,
        toDate: v.toDate,
        halfDay: v.halfDay,
        reason: v.reason.trim() || undefined,
      };
      if (editing) {
        await api.patch(`/api/v1/hr/leave/${editing.id}`, { version: editing.version, ...body });
        return { id: editing.id, documentNo: editing.documentNo, days: null as string | null };
      }
      return api.post<{ id: string; documentNo: string; days: string | null }>('/api/v1/hr/leave', {
        employeeId: v.employeeId,
        ...body,
      });
    },
    onSuccess: (r) => {
      draft.forget();
      onSaved(
        editing
          ? `${r.documentNo} saved. It still needs someone else's approval.`
          : `${r.documentNo} recorded${r.days ? ` (${daysText(r.days)} ${Number(r.days) === 1 ? 'day' : 'days'})` : ''}. Someone else must approve it.`,
        r.id,
      );
    },
    onError: (e) => setProblem(errorMessage(e, 'Could not save')),
  });

  function submit() {
    if (!v.employeeId) return setProblem('Choose the person.');
    if (!v.leaveTypeId) return setProblem('Choose which leave.');
    if (!v.fromDate || !v.toDate) return setProblem('Choose the first and last day.');
    if (v.toDate < v.fromDate) return setProblem('The last day cannot be before the first.');
    if (v.fromDate.slice(0, 4) !== v.toDate.slice(0, 4)) return setProblem('Leave cannot run across two years. Record it as two.');
    setProblem(null);
    save.mutate();
  }

  return (
    <FormPanel
      title={editing ? `Change ${editing.documentNo}` : 'Record leave'}
      icon={<AppIcon kind="leave" />}
      intro="Someone other than you approves it. Once approved, its days go on the attendance register as leave. Weekly offs and holidays inside the dates are not counted."
    >
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <DraftNotice draft={draft} onStartAgain={startAgain} />

        <FormGroup title="Who and which leave" columns={2}>
          {editing ? (
            <Field label="Person" value={`${editing.employeeCode} — ${editing.employeeName}`} onChange={() => {}} disabled />
          ) : (
            <Select
              label="Person"
              required
              value={v.employeeId}
              onChange={(id) => setV((s) => ({ ...s, employeeId: id, leaveTypeId: '' }))}
              options={(people.data?.items ?? []).map((e) => ({ value: e.id, label: employeeLabel(e) }))}
            />
          )}
          <Select label="Leave" required value={v.leaveTypeId} onChange={set('leaveTypeId')} options={typeOptions} />
        </FormGroup>
        <FormGroup
          title="When"
          columns={3}
          hint={
            span > 0
              ? v.halfDay
                ? 'Half a day.'
                : `Up to ${span} ${span === 1 ? 'day' : 'days'}. Weekly offs and holidays inside these dates are not counted.`
              : undefined
          }
        >
          <Field label="First day" type="date" required value={v.fromDate} onChange={set('fromDate')} />
          <Field label="Last day" type="date" required value={v.toDate} onChange={set('toDate')} />
          <label className="settings__toggle leave__halfday" title={v.fromDate !== v.toDate ? 'A half day is a single day' : undefined}>
            <input
              type="checkbox"
              checked={v.halfDay}
              disabled={v.fromDate !== v.toDate}
              onChange={(e) => setV((s) => ({ ...s, halfDay: e.target.checked }))}
            />
            <span className="field__label">Half day only</span>
          </label>
        </FormGroup>
        {v.employeeId && v.leaveTypeId && (
          <div className={`leave__balance${short ? ' leave__balance--short' : ''}`} role="status">
            {balances.isPending ? (
              <span className="muted">Checking the balance…</span>
            ) : !balance ? (
              <span className="muted">No balance found for this leave.</span>
            ) : !balance.isPaid ? (
              <span>{balance.nameEn} is unpaid — no balance is used.</span>
            ) : (
              <span>
                <strong>
                  {daysText(balance.remaining)} {Number(balance.remaining) === 1 ? 'day' : 'days'} of {balance.nameEn} left in {year}
                </strong>{' '}
                ({daysText(balance.entitled)} entitled + {daysText(balance.carriedForward)} brought forward
                {Number(balance.adjusted) !== 0 &&
                  ` ${Number(balance.adjusted) > 0 ? '+' : '−'} ${daysText(Math.abs(Number(balance.adjusted)))} adjusted`}{' '}
                − {daysText(balance.taken)} taken)
                {Number(balance.pending) > 0 && `. ${daysText(balance.pending)} more waiting for a decision`}.
                {short && ' This is more than is left. Record the rest as unpaid leave.'}
              </span>
            )}
          </div>
        )}
        <FormGroup title="Why">
          <TextArea label="Reason" value={v.reason} onChange={set('reason')} rows={2} placeholder="e.g. brother's wedding" />
        </FormGroup>
        {problem && <ErrorNote message={problem} />}
        <FormActions onCancel={onCancel}>
          <button type="submit" className="btn btn--primary" disabled={save.isPending}>
            {editing ? 'Save changes' : 'Record for approval'}
          </button>
        </FormActions>
      </form>
    </FormPanel>
  );
}
